import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { X, Cpu, CheckCircle, FolderKanban } from "lucide-react";
import { projetsAPI, noeudsAPI, automatisationsAPI } from "../api/services";
import { formatDateLong } from "../utils/dateUtils";
import { useSocket } from "../context/SocketContext";
import NodeCard from "../components/widgets/NodeCard";
import ManualOverrideModal from "../components/modals/ManualOverrideModal";

export default function ProjetDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [projet, setProjet] = useState(null);
  const [noeuds, setNoeuds] = useState([]);
  const [loading, setLoading] = useState(true);

  const { lastStatusChange, lastSensorData } = useSocket();

  // Charger projet + nœuds
  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, nRes] = await Promise.all([
          projetsAPI.getOne(id),
          noeudsAPI.getAll({ projet: id }),
        ]);
        setProjet(pRes.data.data);
        setNoeuds(nRes.data.data || []);
      } catch (err) {
        console.error("Erreur chargement projet:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // Mise à jour temps réel — statut
  useEffect(() => {
    if (!lastStatusChange) return;
    setNoeuds(prev => prev.map(n =>
      n.sn === lastStatusChange.sn
        ? { ...n, etat: lastStatusChange.etat, derniereMaj: new Date().toISOString() }
        : n
    ));
  }, [lastStatusChange]);

  // Mise à jour temps réel — données capteur
  useEffect(() => {
    if (!lastSensorData) return;
    setNoeuds(prev => prev.map(n =>
      n.sn === lastSensorData.sn
        ? {
            ...n,
            etat: 'En ligne',
            derniereValeur: lastSensorData.value ?? lastSensorData.valeur ?? n.derniereValeur,
            unite: lastSensorData.unit ?? lastSensorData.unite ?? n.unite,
            derniereMaj: new Date().toISOString(),
          }
        : n
    ));
  }, [lastSensorData]);

  // Modal override manuel
  const [overrideModal, setOverrideModal] = useState(null); // { nodeId, sn, type, activate, automations }

  const executeCommand = useCallback(async (nodeId, sn, activate, pauseAutoIds = []) => {
    try {
      await noeudsAPI.command(nodeId, {
        action: activate ? "Activer" : "Désactiver",
        pauseAutomatisations: pauseAutoIds
      });
      setNoeuds(prev => prev.map(n =>
        n._id === nodeId
          ? { ...n, derniereValeur: activate ? 1 : 0, derniereMaj: new Date().toISOString() }
          : n
      ));
    } catch (err) {
      console.error("Erreur commande actionneur:", err);
    }
  }, []);

  // Toggle actionneur ON/OFF — vérifie les autos avant d'exécuter
  const handleToggle = useCallback(async (nodeId, sn, activate) => {
    try {
      const res = await automatisationsAPI.byActionneurs({ actionneurs: nodeId, projet: id });
      const autos = res.data?.data || [];
      if (autos.length > 0) {
        const noeud = noeuds.find(n => n._id === nodeId);
        setOverrideModal({ nodeId, sn, type: noeud?.type || '', activate, automations: autos });
      } else {
        await executeCommand(nodeId, sn, activate);
      }
    } catch (err) {
      // Si erreur sur la vérification, exécuter quand même
      await executeCommand(nodeId, sn, activate);
    }
  }, [id, noeuds, executeCommand]);

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <span className="ml-3 text-sm text-slate-500">{t('projectDetail.loading')}</span>
        </div>
      </>
    );
  }

  if (!projet) {
    return (
      <>
        <div className="text-center py-32 text-slate-400">{t('projectDetail.notFound')}</div>
      </>
    );
  }

  const capteurs = noeuds.filter(n => n.categorie === "Capteur");
  const actionneurs = noeuds.filter(n => n.categorie === "Actionneur");
  const passerelles = noeuds.filter(n => n.categorie === "Passerelle");
  const actifs = noeuds.filter(n => n.etat === "En ligne").length;

  return (
    <>
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center">
              <FolderKanban size={22} className="text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">{projet.nom}</h1>
              <p className="text-sm text-slate-400 mt-0.5">
                ID: {projet._id?.slice(-8)} — {projet.revendeur?.nom || "—"} → {projet.client?.nom || "—"}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate("/projets")}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-slate-400"
          >
            <X size={20} />
          </button>
        </div>

        {/* Infos */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-5">
          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <p className="text-xs text-slate-400">{t('projectDetail.startDate')}</p>
            <p className="text-sm font-semibold text-slate-700 mt-0.5">
              {formatDateLong(projet.dateDebut)}
            </p>
          </div>
          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <p className="text-xs text-slate-400">{t('projectDetail.state')}</p>
            <span className={`inline-block mt-1 px-2.5 py-0.5 rounded-lg text-xs font-semibold ${
              projet.etat === "Actif" ? "bg-green-100 text-green-700" :
              projet.etat === "Terminé" ? "bg-gray-100 text-gray-600" :
              "bg-blue-100 text-blue-700"
            }`}>
              {projet.etat}
            </span>
          </div>
          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <p className="text-xs text-slate-400">{t('projectDetail.projectManager')}</p>
            {(projet.chefDeProjet || []).length > 0
              ? projet.chefDeProjet.map((m, i) => <p key={i} className="text-sm font-semibold text-slate-700 mt-0.5">{m.nom || m}</p>)
              : <p className="text-sm font-semibold text-slate-700 mt-0.5">—</p>
            }
          </div>
          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <p className="text-xs text-slate-400">{t('projectDetail.technicalLead')}</p>
            {(projet.responsableTechnique || []).length > 0
              ? projet.responsableTechnique.map((m, i) => <p key={i} className="text-sm font-semibold text-slate-700 mt-0.5">{m.nom || m}</p>)
              : <p className="text-sm font-semibold text-slate-700 mt-0.5">—</p>
            }
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-1">
            <Cpu size={14} className="text-blue-500" />
            <span className="text-xs font-medium text-slate-400">{t('projectDetail.devices')}</span>
          </div>
          <p className="text-3xl font-bold text-slate-800">{noeuds.length}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle size={14} className="text-green-500" />
            <span className="text-xs font-medium text-slate-400">{t('projectDetail.active')}</span>
          </div>
          <p className="text-3xl font-bold text-slate-800">{actifs}</p>
        </div>
      </div>

      {/* Nœuds du projet */}
      <div className="mb-4">
        <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wide">
          {t('projectDetail.projectNodes')} ({noeuds.length})
        </h2>
      </div>

      {noeuds.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
          <Cpu size={32} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-400">{t('projectDetail.noNodes')}</p>
          <p className="text-xs text-slate-300 mt-1">{t('projectDetail.createSiteHint')}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Capteurs */}
          {capteurs.length > 0 && (
            <div>
              <span className="inline-block px-3 py-1 rounded-lg text-xs font-semibold bg-blue-100 text-blue-700 mb-3">
                {t('projectDetail.sensors')} ({capteurs.length})
              </span>
              <div className="flex flex-wrap gap-4">
                {capteurs.map(n => <NodeCard key={n._id} noeud={n} />)}
              </div>
            </div>
          )}

          {/* Actionneurs */}
          {actionneurs.length > 0 && (
            <div>
              <span className="inline-block px-3 py-1 rounded-lg text-xs font-semibold bg-green-100 text-green-700 mb-3">
                {t('projectDetail.actuators')} ({actionneurs.length})
              </span>
              <div className="flex flex-wrap gap-4">
                {actionneurs.map(n => <NodeCard key={n._id} noeud={n} onToggle={handleToggle} />)}
              </div>
            </div>
          )}

          {/* Passerelles (Connexion) */}
          {passerelles.length > 0 && (
            <div>
              <span className="inline-block px-3 py-1 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700 mb-3">
                {t('projectDetail.connection')} ({passerelles.length})
              </span>
              <div className="flex flex-wrap gap-4">
                {passerelles.map(n => <NodeCard key={n._id} noeud={n} />)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal override manuel */}
      {overrideModal && (
        <ManualOverrideModal
          isOpen={!!overrideModal}
          onClose={() => setOverrideModal(null)}
          actionneur={{ sn: overrideModal.sn, type: overrideModal.type }}
          action={overrideModal.activate ? "Activer" : "Désactiver"}
          automations={overrideModal.automations}
          onConfirm={(pauseIds) => {
            executeCommand(overrideModal.nodeId, overrideModal.sn, overrideModal.activate, pauseIds);
            setOverrideModal(null);
          }}
        />
      )}
    </>
  );
}
